import { createAction, createSlice } from '@reduxjs/toolkit';

export const createUserRequest = createAction('user/createUserRequest');
export const deleteUserRequest = createAction('user/deleteUserRequest');
export const updateUserAttributesRequest = createAction('user/updateUserAttributesRequest');
export const changePasswordRequest = createAction("user/changePasswordRequest");
export const getUserDataRequest = createAction('user/getUserDataRequest');

const userSlice = createSlice({
  name: 'user',
  initialState: {
    byId: {},
    allIds: [],
    message: undefined,
    errMsg: undefined,
    loading: false,
    createUserButtonLoading: false,
    error: null,
    foundEmail: [],
    foundPhoneNumber: [],
    currentProductId: "",
    userStatus: false,

    userId: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    enabled: false,
    userCreateDate: "",
    userLastModifiedDate: "",
    userStatus: "",
  },
  reducers: {
    createUserStart: state => {
      state.loading = true;
      state.createUserButtonLoading = true;
      state.error = null;
      state.message = null;
      state.errMsg = null;
      state.userStatus = false;
    },
    createUserSuccess: (state, action) => {
      const { id, user, status, keepButtonLoading } = action.payload;
      state.byId[id] = user;
      if (!state.allIds?.includes(id)) {
        state.allIds.push(id);
      }
      state.message = user.message;
      state.userStatus = status;
      state.errMsg = null;
      state.loading = false;
      if (!keepButtonLoading) {
        state.createUserButtonLoading = false;
      }
    },
    createUserFailure: (state, action) => {
      state.loading = false;
      state.createUserButtonLoading = false;
      state.error = action.payload || 'Failed to create user';
      state.userStatus = false;
      state.message = undefined;
      state.errMsg = undefined;
    },
    clearUserState: state => {
      state.byId = {};
      state.allIds = [];
      state.message = undefined;
      state.errMsg = undefined;
      state.loading = false;
      state.error = null;

      state.foundPhoneNumber = null;
      state.foundEmail = null;
      state.currentProductId = "";
      state.userStatus = false;
      state.userId = "";
      state.firstName = "";
      state.lastName = "";
      state.email = "";
      state.phone = "";
      state.address = "";
      state.enabled = false;
      state.userCreateDate = "";
      state.userLastModifiedDate = "";
      state.userStatus = "";
    },

    deleteUserStart: state => {
      state.loading = true;
      state.error = null;
    },
    deleteUserSuccess: (state, action) => {
      const id = action.payload;
      delete state.byId[id];
      state.allIds = state.allIds.filter(uid => uid !== id);
      state.loading = false;
    },
    deleteUserFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to delete user';
    },

    // Find by phone number reducers
    findUserStart: (state) => {
      state.loading = true;
      state.error = null;
      state.foundPhoneNumber = null;
      state.foundEmail = null;

      state.userId = "";
      state.firstName = "";
      state.lastName = "";
      state.email = "";
      state.phone = "";
      state.address = "";
      state.enabled = false;
      state.userCreateDate = "";
      state.userLastModifiedDate = "";
      state.userStatus = "";
    },
    findUserSuccess: (state, action) => {
      const user = action.payload;
      if (!user) {
        state.loading = false;
        state.error = 'No user found';
        return;
      }
    
      const attrs = user.userAttributes
      const userAddress = JSON.parse(attrs.address || "{}");
    
      state.userId = attrs?.sub ?? "";
      state.firstName = attrs.firstName ?? "";
      state.lastName = attrs.lastName ?? "";
      state.email = attrs.email;
      state.phone = attrs.phoneNumber ?? "";
      state.address = [
        userAddress.line1,
        userAddress.line2,
        userAddress.landmark,
        userAddress.city,
        userAddress.state,
        userAddress.country,
        userAddress.pincode
      ].filter(Boolean).join(', ');
      state.enabled = user.emailVerified || user.phoneNumberVerified;
      state.userCreateDate = user.UserCreateDate ?? "";
      state.userLastModifiedDate = user.UserLastModifiedDate ?? "";
      state.userStatus = user.UserStatus ?? "";
      state.loading = false;
    },
    findUserFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to find user';

      state.foundPhoneNumber = null;
      state.foundEmail = null;
      state.userId = "";
      state.firstName = "";
      state.lastName = "";
      state.email = "";
      state.phone = "";
      state.address = "";
      state.enabled = false;
      state.userCreateDate = "";
      state.userLastModifiedDate = "";
      state.userStatus = "";
    },
    updateUserAttributesStart: state => {
      state.updating = true;
      state.error = null;
    },
    updateUserAttributesSuccess: (state, action) => {
      const { firstName, lastName } = action.payload;
      state.firstName = firstName;
      state.lastName = lastName;
      state.updating = false;
    },
    updateUserAttributesFailure: (state, action) => {
      state.updating = false;
      state.error = action.payload;
    },
    changePasswordStart(state) {
      state.loading = true;
      state.error = null;
    },
    changePasswordSuccess(state) {
      state.loading = false;
      state.error = null;
    },
    changePasswordFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    setCreateUserButtonLoading: (state, action) => {
      state.createUserButtonLoading = action.payload;
    },

    setCurrentProductId: (state, action) => {
      state.currentProductId = action.payload;
    },
  },
});

export const {
  createUserStart,
  createUserSuccess,
  createUserFailure,
  clearUserState,
  deleteUserStart,
  deleteUserSuccess,
  deleteUserFailure,
  findUserStart,
  findUserSuccess,
  findUserFailure,
  updateUserAttributesStart,
  updateUserAttributesSuccess,
  updateUserAttributesFailure,
  changePasswordStart,
  changePasswordSuccess,
  changePasswordFailure,
  setCreateUserButtonLoading,
  setCurrentProductId,
} = userSlice.actions;

export default userSlice.reducer;
